import type { InstallStatus } from '@totum-analytics/db';
import { DASHBOARD_POLL_MS } from '@totum-analytics/shared/config';
import { TRACK_CALL_EXAMPLE, type Snippet } from '@totum-analytics/shared/snippet';
import { Badge, Card, CopyButton, ErrorNote, Loading } from '../components/ui';
import { formatRelative } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useSite } from './SiteLayout';

function Code({ children }: { children: string }) {
  return (
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto leading-relaxed">
      <code>{children}</code>
    </pre>
  );
}

const STATE: Record<InstallStatus['state'], { tone: 'yellow' | 'green'; title: string; body: string }> = {
  waiting_for_events: {
    tone: 'yellow',
    title: 'Waiting for the first event',
    body: 'Add the tag, then load any page on the site. This updates by itself within a few seconds.',
  },
  waiting_for_identify: {
    tone: 'yellow',
    title: 'Events are arriving, but nobody is identified yet',
    body: 'The tag works. Now add the identify call after sign-in, then sign in to the app. Until then, usage cannot be attributed to people or accounts.',
  },
  receiving: { tone: 'green', title: 'Receiving data', body: 'Everything is wired up.' },
};

export function Install() {
  const { site } = useSite();
  const snippet = useApi<Snippet>(`/sites/${site.id}/snippet`);
  const status = useApi<InstallStatus>(`/sites/${site.id}/install-status`, { pollMs: DASHBOARD_POLL_MS });
  const state = status.data ? STATE[status.data.state] : null;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <Card title="1. Add the tag" actions={snippet.data && <CopyButton text={snippet.data.scriptTag} />}>
          <p className="text-sm text-gray-600 mb-3">
            Paste this inside <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code> on every page, or once in your app's root HTML. Keep{' '}
            <code className="bg-gray-100 px-1 rounded">defer</code>: it is what keeps the tracker off your page's critical path.
          </p>
          {snippet.error && <ErrorNote error={snippet.error} onRetry={snippet.reload} />}
          {snippet.data ? <Code>{snippet.data.scriptTag}</Code> : <Loading />}
        </Card>

        {snippet.data?.identifyCall && (
          <Card title="2. Identify the signed-in user" actions={<CopyButton text={snippet.data.identifyCall} />}>
            <p className="text-sm text-gray-600 mb-3">
              Call this once sign-in completes, and on each page load where the user is already signed in. Pass ids only — the
              Cognito <code className="bg-gray-100 px-1 rounded">sub</code> and the organisation id — never names or email addresses.
              It is safe to call before the tag has loaded.
            </p>
            <Code>{snippet.data.identifyCall}</Code>
          </Card>
        )}

        <Card title={snippet.data?.identifyCall ? '3. Optional: feature events' : '2. Optional: custom events'} actions={<CopyButton text={TRACK_CALL_EXAMPLE} />}>
          <p className="text-sm text-gray-600 mb-3">
            Feature adoption is built from these. Name them after the feature, not the button, in lowercase with underscores.
          </p>
          <Code>{TRACK_CALL_EXAMPLE}</Code>
        </Card>

        <Card title="For coding agents">
          <p className="text-sm text-gray-600">
            Point an agent at <code className="bg-gray-100 px-1 rounded">{snippet.data?.scriptTag.match(/src="([^"]+)\/t\.js"/)?.[1]}/install.md</code>.
            It is a plain-text guide written for an agent to follow, and it says exactly the same as this page.
          </p>
        </Card>
      </div>

      <div className="space-y-6">
        <Card title="Status">
          {status.error && <ErrorNote error={status.error} onRetry={status.reload} />}
          {state && status.data ? (
            <div aria-live="polite">
              <Badge tone={state.tone} icon={state.tone === 'green' ? '✓' : '…'}>{state.title}</Badge>
              <p className="mt-3 text-sm text-gray-600">{state.body}</p>
              <dl className="mt-4 text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                <dt className="text-gray-500">First event</dt>
                <dd className="text-gray-900">{formatRelative(status.data.first_event_at)}</dd>
                <dt className="text-gray-500">Last event</dt>
                <dd className="text-gray-900">{formatRelative(status.data.last_event_at)}</dd>
                {site.identity_mode === 'identified' && (
                  <>
                    <dt className="text-gray-500">Last identified</dt>
                    <dd className="text-gray-900">{formatRelative(status.data.last_identified_at)}</dd>
                  </>
                )}
              </dl>
            </div>
          ) : (
            !status.error && <Loading />
          )}
        </Card>

        <Card title="Nothing arriving?">
          <ul className="text-sm text-gray-600 list-disc pl-5 space-y-2">
            <li>Check the page's hostname is one of: <span className="font-mono text-xs">{site.hostnames.join(', ')}</span>. Beacons from anywhere else are ignored.</li>
            <li>Add <code className="bg-gray-100 px-1 rounded">data-debug="true"</code> to the tag and watch the browser console.</li>
            <li>A site that is paused in Site settings accepts nothing.</li>
            <li>Changes to a site take up to a minute to reach the edge.</li>
            <li>Ad blockers can block analytics; try a private window without extensions.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
